import { flatten, isEqual, sortBy } from "lodash";
import { CommonHelper } from "../../../../../../react-core/Helpers/CommonHelper";
import { RegulatoryCheckInfoResult } from "../../../../../../Wrap/Constants/Enums/Customer/RegulatoryCheckInfoResult";
import { RegulatoryCheckInfoStatus } from "../../../../../../Wrap/Constants/Enums/Customer/RegulatoryCheckInfoStatus";
import { RegulatoryCheckInfoType } from "../../../../../../Wrap/Constants/Enums/Customer/RegulatoryCheckInfoType";
import { EmailAddressType } from "../../../../../../Wrap/Constants/Enums/EmailAddressType";
import { RoleType } from "../../../../../../Wrap/Constants/Enums/RoleType";
import { AccountRoleByActorDetails } from "../../../../../../Wrap/Models/Domains/Account/AccountRoleByActorDetails";
import { CustomerMerge } from "../../../../../../Wrap/Models/Models/Customer/CustomerMerge";
import { CustomerTaxResidencyModel } from "../../../../../../Wrap/Models/Models/Customer/Regulatory/CustomerTaxResidencyModel";
import { CountryCodeMap } from "../../../../../Shared/Entities/Maps/CountryCodeMap";
import { getValueByKey } from "../../../../../Shared/Table/Utils/TableUtils";
import { RegulatoryCheckResultTypeMap } from "../../ClientRecord/Resource/Contents";

export const matchSpecificField = (data1: any, data2: any): boolean => {
    data1 = CommonHelper.isNullOrEmpty(data1) ? null : data1;
    data2 = CommonHelper.isNullOrEmpty(data2) ? null : data2;
    return data1 === data2;
};

export const getNationality = (tData: Array<CustomerTaxResidencyModel>): Array<string> => {
    const result = [];
    tData = tData || [];
    tData?.forEach((e) => {
        if (e?.IsNationality) {
            result.push(CountryCodeMap.getString(e?.Country));
        }
    });
    return result;
};

export const fieldDefaultValue = (fieldData: any): string => {
    if (CommonHelper.isNullOrEmpty(fieldData)) {return "Not held";}
    return fieldData || "Not held";
};

export const renderYesOrNo = (boolean: boolean): string => {
    return boolean ? "Yes" : boolean == false ? "No" : "Not held";
};

export const switchTypes = (type: string) => {
    switch (type) {
        case "customer-details":
            return [
                "PersonalDetails.Title",
                "PersonalDetails.GivenName",
                "PersonalDetails.FamilyName",
                "PersonalDetails.Gender",
                "PersonalDetails.MaritalStatus",
                "PersonalDetails.DateOfBirth",
                "PersonalDetails.TownOfBirth",
                "CountryOfNationality",
                "PersonalDetails.CountryOfBirth",
                "EmploymentDetails.IndividualEmploymentStatus",
            ];
        case "contact-information":
            return ["PhoneNumber", "Email"];
        case "addresses":
            return ["Addresses"];
        case "regulatory":
            return ["Regulatory"];
        case "regulatory-checks":
            return ["RegulatoryChecks"];
    }
};

export const doCompare = (types: Array<string>, customer1: CustomerMerge, customer2: CustomerMerge) => {
    const result = types?.map((e) => {
        if (e === "CountryOfNationality") {
            const nationality1 = getNationality(customer1?.CustomerTaxResidencyDetails);
            const _nationality1 = nationality1?.sort((a, b) => {
                if (a?.toUpperCase() > b?.toUpperCase()) {
                    return 1;
                }
                if (a?.toUpperCase() < b?.toUpperCase()) {
                    return -1;
                }
                return 0;
            });
            const nationality2 = getNationality(customer2?.CustomerTaxResidencyDetails);
            const _nationality2 = nationality2?.sort((a, b) => {
                if (a?.toUpperCase() > b?.toUpperCase()) {
                    return 1;
                }
                if (a?.toUpperCase() < b?.toUpperCase()) {
                    return -1;
                }
                return 0;
            });
            return isEqual(_nationality1, _nationality2);
        } else if (e === "PhoneNumber") {
            const result = [];
            // extract the union set of address type
            const phoneNumberTypeFromCustomer1 = customer1?.PhoneNumberDetails?.map((e) => e?.PhoneNumberType);
            const phoneNumberTypeFromCustomer2 = customer2?.PhoneNumberDetails?.map((e) => e?.PhoneNumberType);

            // declare a new set
            const phoneNumberTypeSet = new Set();
            phoneNumberTypeFromCustomer1?.forEach((e) => phoneNumberTypeSet.add(e));
            phoneNumberTypeFromCustomer2?.forEach((e) => phoneNumberTypeSet.add(e));
            const phoneNumber1 = [];
            const phoneNumber2 = [];
            for (const ele of phoneNumberTypeSet) {
                // find if type already existed
                const _data1 = customer1?.PhoneNumberDetails?.find((e) => e?.PhoneNumberType == ele);
                if (_data1) {
                    phoneNumber1.push(_data1);
                } else {
                    // need create empty object
                    phoneNumber1.push({ PhoneNumberType: null, PhoneNumber: null });
                }

                const _data2 = customer2?.PhoneNumberDetails?.find((e) => e?.PhoneNumberType == ele);
                if (_data2) {
                    phoneNumber2.push(_data2);
                } else {
                    // need create empty object
                    phoneNumber2.push({ PhoneNumberType: null, PhoneNumber: null });
                }
            }

            phoneNumber1?.forEach((e, i) => {
                result.push(matchSpecificField(e?.PhoneNumberType, phoneNumber2[i]?.PhoneNumberType));
                result.push(matchSpecificField(e?.PhoneNumber?.Country, phoneNumber2[i]?.PhoneNumber?.Country));
                result.push(
                    matchSpecificField(
                        (e?.PhoneNumber?.AreaCode || "") + (e?.PhoneNumber?.Number || ""),
                        (phoneNumber2[i]?.PhoneNumber?.AreaCode || "") + (phoneNumber2[i]?.PhoneNumber?.Number || "")
                    )
                );
            });
            return result;
        } else if (e === "Email") {
            const result = [];
            const primaryEmail1 = customer1?.EmailAddressDetails?.find(
                (e) => e?.EmailAddressType == EmailAddressType.Primary
            );
            const primaryEmail2 = customer2?.EmailAddressDetails?.find(
                (e) => e?.EmailAddressType == EmailAddressType.Primary
            );
            const otherEmail1 = customer1?.EmailAddressDetails?.find(
                (e) => e?.EmailAddressType == EmailAddressType.Other
            );
            const otherEmail2 = customer2?.EmailAddressDetails?.find(
                (e) => e?.EmailAddressType == EmailAddressType.Other
            );
            result.push(matchSpecificField(primaryEmail1?.EmailAddress, primaryEmail2?.EmailAddress));
            result.push(matchSpecificField(otherEmail1?.EmailAddress, otherEmail2?.EmailAddress));
            return result;
        } else if (e === "Addresses") {
            const result = [];
            // extract the union set of address type
            const addressTypeFromCustomer1 = customer1?.AddressDetails?.map((e) => e?.PhysicalAddressType);
            const addressTypeFromCustomer2 = customer2?.AddressDetails?.map((e) => e?.PhysicalAddressType);

            // declare a new set
            const addressTypeSet = new Set();
            addressTypeFromCustomer1?.forEach((e) => addressTypeSet.add(e));
            addressTypeFromCustomer2?.forEach((e) => addressTypeSet.add(e));
            const address1 = [];
            const address2 = [];
            for (const ele of addressTypeSet) {
                // find if type already existed
                const _data1 = customer1?.AddressDetails?.find((e) => e?.PhysicalAddressType == ele);
                if (_data1) {
                    address1.push(_data1);
                } else {
                    // need create empty object
                    address1.push({ PhysicalAddressType: null, Address: null });
                }

                const _data2 = customer2?.AddressDetails?.find((e) => e?.PhysicalAddressType == ele);
                if (_data2) {
                    address2.push(_data2);
                } else {
                    // need create empty object
                    address2.push({ PhysicalAddressType: null, Address: null });
                }
            }

            address1?.forEach((e, i) => {
                result.push(matchSpecificField(e?.PhysicalAddressType, address2[i]?.PhysicalAddressType));
                result.push(matchSpecificField(e?.Address?.Address1, address2[i]?.Address?.Address1));
                result.push(matchSpecificField(e?.Address?.Address2, address2[i]?.Address?.Address2));
                result.push(matchSpecificField(e?.Address?.Address3, address2[i]?.Address?.Address3));
                result.push(matchSpecificField(e?.Address?.Address4, address2[i]?.Address?.Address4));
                result.push(matchSpecificField(e?.Address?.PostCode, address2[i]?.Address?.PostCode));
            });
            return result;
        } else if (e === "Regulatory") {
            const result = [];
            const tax1 = customer1?.CustomerTaxResidencyDetails?.sort((a, b) => {
                if (a?.Country?.toString() > b?.Country?.toString()) {
                    return 1;
                }
                if (a?.Country?.toString() < b?.Country?.toString()) {
                    return -1;
                }
                return 0;
            });
            const tax2 = customer2?.CustomerTaxResidencyDetails?.sort((a, b) => {
                if (a?.Country?.toString() > b?.Country?.toString()) {
                    return 1;
                }
                if (a?.Country?.toString() < b?.Country?.toString()) {
                    return -1;
                }
                return 0;
            });

            // check the length and keep them to the same length
            const lengthDifference = tax1?.length - tax2?.length;
            if (lengthDifference > 0) {
                // phone1 is greater or equal than phone Number2
                for (let i = 0; i < lengthDifference; i++) {
                    tax2?.push({
                        Country: null,
                        IsTaxResident: null,
                        PrimaryCitizenship: null,
                        PrimaryTaxResidency: null,
                        NationalIdentifier: null,
                        PassportNumber: null,
                        TaxReferenceType: null,
                        TaxReference: null,
                        TaxResidencyForPensionRelief: null,
                        TaxExempt: null,
                        TaxExemptionReason: null,
                        IsNationality: null,
                    });
                }
            } else {
                // phone1 is less than phone2
                for (let i = 0; i < lengthDifference * -1; i++) {
                    tax1?.push({
                        Country: null,
                        IsTaxResident: null,
                        PrimaryCitizenship: null,
                        PrimaryTaxResidency: null,
                        NationalIdentifier: null,
                        PassportNumber: null,
                        TaxReferenceType: null,
                        TaxReference: null,
                        TaxResidencyForPensionRelief: null,
                        TaxExempt: null,
                        TaxExemptionReason: null,
                        IsNationality: null,
                    });
                }
            }

            tax1?.forEach((e, i) => {
                result.push(matchSpecificField(e?.Country, tax2[i]?.Country));
                result.push(matchSpecificField(e?.IsNationality, tax2[i]?.IsNationality));
                result.push(matchSpecificField(e?.IsTaxResident, tax2[i]?.IsTaxResident));
                result.push(matchSpecificField(e?.PrimaryTaxResidency, tax2[i]?.PrimaryTaxResidency));
                result.push(matchSpecificField(e?.PrimaryCitizenship, tax2[i]?.PrimaryCitizenship));
                result.push(matchSpecificField(e?.NationalIdentifier, tax2[i]?.NationalIdentifier));
                result.push(matchSpecificField(e?.PassportNumber, tax2[i]?.PassportNumber));
                result.push(matchSpecificField(e?.TaxReference, tax2[i]?.TaxReference));
                result.push(matchSpecificField(e?.TaxReferenceType, tax2[i]?.TaxReferenceType));
            });
            return result;
        } else if (e === "RegulatoryChecks") {
            const result = [];
            const aml1 = customer1?.RegulatoryCheck?.find((e) => e?.RegulatoryCheckType == RegulatoryCheckInfoType.AML);
            const pep1 = customer1?.RegulatoryCheck?.find((e) => e?.RegulatoryCheckType == RegulatoryCheckInfoType.PEP);
            const aml2 = customer2?.RegulatoryCheck?.find((e) => e?.RegulatoryCheckType == RegulatoryCheckInfoType.AML);
            const pep2 = customer2?.RegulatoryCheck?.find((e) => e?.RegulatoryCheckType == RegulatoryCheckInfoType.PEP);
            result.push(matchSpecificField(aml1?.ServiceProvider, aml2?.ServiceProvider));
            result.push(matchSpecificField(aml1?.RegulatoryCheckStatus, aml2?.RegulatoryCheckStatus));
            result.push(matchSpecificField(aml1?.RegulatoryCheckResult, aml2?.RegulatoryCheckResult));
            result.push(matchSpecificField(pep1?.RegulatoryCheckStatus, pep2?.RegulatoryCheckStatus));
            result.push(
                matchSpecificField(
                    pep1 == null || pep1?.RegulatoryCheckResult == null
                        ? " "
                        : pep1?.RegulatoryCheckStatus == RegulatoryCheckInfoStatus.NotVetted ||
                          pep1?.RegulatoryCheckStatus == RegulatoryCheckInfoStatus.SelfCertified
                        ? "N/A"
                        : RegulatoryCheckResultTypeMap[pep1?.RegulatoryCheckResult],
                    pep2 == null || pep2?.RegulatoryCheckResult == null
                        ? " "
                        : pep2?.RegulatoryCheckStatus == RegulatoryCheckInfoStatus.NotVetted ||
                          pep2?.RegulatoryCheckStatus == RegulatoryCheckInfoStatus.SelfCertified
                        ? "N/A"
                        : RegulatoryCheckResultTypeMap[pep2?.RegulatoryCheckResult]
                )
            );

            return result;
        } else {
            return matchSpecificField(getValueByKey(customer1, e), getValueByKey(customer2, e));
        }
    });

    return flatten(result);
};

export const validateJointAccountHolder = (
    accountRole1: Array<AccountRoleByActorDetails>,
    accountRole2: Array<AccountRoleByActorDetails>
) => {
    const _jointAccount1 = accountRole1?.filter((e) => e?.AccountRoleType == RoleType.JointAccountHolder);
    const _jointAccount2 = accountRole2?.filter((e) => e?.AccountRoleType == RoleType.JointAccountHolder);
    if (_jointAccount1.length == 0 || _jointAccount2.length == 0) {
        return true;
    }
    const result = _jointAccount1.some((e) => {
        // Find account id in the second list
        const _result = _jointAccount2?.find((ele) => ele?.ClAccountId == e?.ClAccountId);
        return !!_result;
    });
    return !result;
};

export const validateActiveUsers = (userId1: number, userId2: number) => {
    if (userId1 == null || userId2 == null) {return true;}
    return userId1 == userId2;
};

export const validateAMLCheckStatus = (customer1: CustomerMerge, customer2: CustomerMerge) => {
    const aml1 = customer1?.RegulatoryCheck?.find((e) => e?.RegulatoryCheckType == RegulatoryCheckInfoType.AML);
    const aml2 = customer2?.RegulatoryCheck?.find((e) => e?.RegulatoryCheckType == RegulatoryCheckInfoType.AML);
    const result = [];
    if (aml1?.RegulatoryCheckResult != RegulatoryCheckInfoResult.Pass) {
        result.push(
            `${customer1?.PersonalDetails?.GivenName} ${customer1?.PersonalDetails?.FamilyName} - ${customer1?.ActorId}`
        );
    }
    if (aml2?.RegulatoryCheckResult != RegulatoryCheckInfoResult.Pass) {
        result.push(
            `${customer2?.PersonalDetails?.GivenName} ${customer2?.PersonalDetails?.FamilyName} - ${customer2?.ActorId}`
        );
    }

    return result;
};
